import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useRemote } from "./remote-context";
import { msalInstance } from "../helpers/msalConfig";
import apiService from "../api/api-service";
import { contentqaMsalInstance } from "../helpers/msalConfigModule";
import { ALLOWED_REGIONS, setRegion, type Region } from "../api/region-config";

type RegionStatus = "loading" | "ready" | "missing" | "invalid";
type AllowedLanguage = "en" | "de" | "ja";

type SessionDetails = {
  work_session_id: string;
  work_group_id: string;
  project_id: string;
  client_id: string;
  user_id: string;
  accessToken: string;
  region: string;
  language?: string;
  enterpriseid: string;
  userDetails: {
    name: string;
    email: string;
    givenName: string;
  };
};

interface SessionContextType {
  sessionDetails: SessionDetails;
  setSessionDetails: React.Dispatch<React.SetStateAction<SessionDetails>>;
  getToken: () => Promise<any>;
  getModuleToken: () => Promise<any>;
  getPlatformToken: () => Promise<any>;
  isJoinedRoom: boolean;
  setIsJoinedRoom: React.Dispatch<React.SetStateAction<boolean>>;
  pageContent: string;
  setPageContent: React.Dispatch<React.SetStateAction<string>>;
  roleName: string;
  setRoleName: React.Dispatch<React.SetStateAction<string>>;
  isApproved: boolean;
  setIsApproved: React.Dispatch<React.SetStateAction<boolean>>;
  hasAccessError: boolean;
  setHasAccessError: React.Dispatch<React.SetStateAction<boolean>>;
  moduleAccessToken: string;
  setModuleAccessToken: React.Dispatch<React.SetStateAction<string>>;
  logout: () => Promise<void>;

  region: Region | null;
  language: AllowedLanguage | "";
  regionStatus: RegionStatus;
  regionError: string;
  availableRegions: Region[];
  selectRegion: (region: Region) => void;
  revalidateRegion: () => void;
}

export const SessionProviderContext = createContext<
  SessionContextType | undefined
>(undefined);

const ALLOWED_LANGUAGES = ["en", "de", "ja"] as const;

// Region-specific allowed languages
const REGION_LANGUAGE_MAP: Record<Region, AllowedLanguage[]> = {
  us: ["en"],
  de: ["de", "en"],
  jp: ["ja", "en"],
};

const validateRegionFromUrl = (
  pathname: string,
  search: string,
): {
  status: RegionStatus;
  region?: Region;
  language?: AllowedLanguage;
  errorMessage?: string;
} => {
  // Block query params if present
  if (search) {
    return {
      status: "invalid",
      errorMessage: "Invalid URL. Query params are not allowed.",
    };
  }

  const segments = pathname.split("/").filter(Boolean);

  /**
   * Allowed:
   * /
   * /us
   * /de
   * /jp
   * /us/en
   * /de/de
   * /de/en
   * /jp/ja
   * /jp/en
   */

  if (segments.length === 0) {
    return { status: "missing" };
  }

  if (segments.length > 2) {
    return {
      status: "invalid",
      errorMessage:
        "Invalid URL. Use /us, /de, /jp and optionally a language like /us/en.",
    };
  }

  const regionSegment = segments[0];

  // Region must be lowercase
  if (regionSegment !== regionSegment.toLowerCase()) {
    return {
      status: "invalid",
      errorMessage: "Invalid region. Region must be lowercase: /us, /de, /jp.",
    };
  }

  if (!ALLOWED_REGIONS.includes(regionSegment as Region)) {
    return {
      status: "invalid",
      errorMessage: "Invalid region. Allowed regions are /us, /de, /jp.",
    };
  }

  // language optional
  if (segments.length === 1) {
    return {
      status: "ready",
      region: regionSegment as Region,
    };
  }

  const languageSegment = segments[1];
  const region = regionSegment as Region;

  // Language must be lowercase
  if (languageSegment !== languageSegment.toLowerCase()) {
    return {
      status: "invalid",
      errorMessage: "Invalid language. Language must be lowercase: en, de, ja.",
    };
  }

  if (!ALLOWED_LANGUAGES.includes(languageSegment as AllowedLanguage)) {
    return {
      status: "invalid",
      errorMessage: "Invalid language. Allowed languages are en, de, ja.",
    };
  }

  // Check if language is allowed for this region
  const allowedLanguagesForRegion = REGION_LANGUAGE_MAP[region];
  if (!allowedLanguagesForRegion.includes(languageSegment as AllowedLanguage)) {
    const availableLanguages = allowedLanguagesForRegion.join(", ");
    return {
      status: "invalid",
      errorMessage: `Invalid language for region ${region}. Allowed languages are: ${availableLanguages}.`,
    };
  }

  return {
    status: "ready",
    region: region,
    language: languageSegment as AllowedLanguage,
  };
};

export const SessionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [sessionDetails, setSessionDetails] = useState<SessionDetails>({
    work_session_id: "",
    work_group_id: "",
    project_id: "",
    client_id: "",
    user_id: "",
    accessToken: "",
    region: "",
    language: "",
    enterpriseid: "",
    userDetails: {
      name: "",
      email: "",
      givenName: "",
    },
  });

  const [moduleAccessToken, setModuleAccessToken] = useState<string>("");
  const [isJoinedRoom, setIsJoinedRoom] = useState<boolean>(false);
  const { instance } = useMsal();
  const { WorkSessionID, WorkGroupID, UserId, ClientId } = useRemote();
  const [pageContent, setPageContent] = useState<string>("dashboard");
  const [roleName, setRoleName] = useState<string>("");
  const [isApproved, setIsApproved] = useState(false);
  const [hasAccessError, setHasAccessError] = useState(false);

  const [region, setSelectedRegion] = useState<Region | null>(null);
  const [language, setLanguage] = useState<AllowedLanguage | "">("");
  const [regionStatus, setRegionStatus] = useState<RegionStatus>("loading");
  const [regionError, setRegionError] = useState("");
  const [failedRegion, setFailedRegion] = useState<Region | null>(null);

  const revalidateRegion = () => {
    const result = validateRegionFromUrl(
      window.location.pathname,
      window.location.search,
    );

    if (result.status === "missing") {
      // Set US as default region
      setSelectedRegion("us");
      setLanguage("");
      setRegion("us"); // runtime region for API config
      setRegionStatus("loading"); // Loading while init API processes
      setRegionError("");

      setSessionDetails((prev) => ({
        ...prev,
        region: "us",
        language: "",
      }));
      return;
    }

    if (result.status === "invalid") {
      setSelectedRegion(null);
      setLanguage("");
      setRegionStatus("invalid");
      setRegionError(result.errorMessage || "Invalid URL");
      return;
    }

    if (result.region) {
      // If region is US, clean the URL
      if (result.region === "us") {
        window.history.replaceState(null, "", "/");
      }

      setSelectedRegion(result.region);
      setLanguage(result.language || "");
      setRegion(result.region); // runtime region for API config
      setRegionStatus("loading"); // Loading while init API processes
      setRegionError("");

      setSessionDetails((prev) => ({
        ...prev,
        region: result.region || "",
        language: result.language || "",
      }));
    }
  };

  const selectRegion = (selectedRegion: Region) => {
    // Update URL with selected region (US stays at /, other regions get /<region>)
    const newUrl = selectedRegion === "us" ? "/" : `/${selectedRegion}`;

    // Reset failed region when selecting a new region
    setFailedRegion(null);

    // Reload app from start with new region
    window.location.href = newUrl;
  };

  useEffect(() => {
    revalidateRegion();

    const handlePopState = () => {
      revalidateRegion();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const getToken = async () => {
    try {
      const authService = await import("mosaic_host_app/authService");
      const tokenResponse = await authService.acquireToken();

      if (tokenResponse?.split(".").length === 3) {
        try {
          const base64Url = tokenResponse.split(".")[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join(""),
          );

          const userDetails = JSON.parse(jsonPayload);

          setSessionDetails((prev) => {
            return {
              ...prev,
              work_session_id: WorkSessionID ?? "",
              work_group_id: WorkGroupID ?? "",
              user_id: UserId ?? "",
              client_id: ClientId ?? "",
              accessToken: tokenResponse,
              enterpriseid: userDetails.enterpriseid || "",
              userDetails: {
                name: userDetails.name || "",
                email: userDetails.unique_name || "",
                givenName: userDetails.given_name || "",
              },
            };
          });

          if (!UserId) {
            setHasAccessError(true);
            return tokenResponse;
          }

          try {
            const userInfo = await apiService.get(
              `user/${UserId}`,
              {},
              "platformUrl",
              tokenResponse,
            );

            if (userInfo.data?.rolename) {
              setRoleName(userInfo.data.rolename);
            }
            if (userInfo.data?.enterpriseid) {
              setSessionDetails((prev) => ({
                ...prev,
                enterpriseid: userInfo.data.enterpriseid,
              }));
            }
          } catch (userInfoError) {
            console.error("Failed to fetch user info:", userInfoError);
            setHasAccessError(true);
          }
        } catch (e) {
          console.warn("Failed to decode access token:", e);
        }
      }

      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const redirectRequest = {
          account: instance.getAllAccounts()[0],
          scopes: ["https://graph.windows.net/User.Read", "openid"],
        };
        instance.acquireTokenRedirect(redirectRequest);
      } else {
        console.log("Error during initial acquireTokenSilent:", error);
      }
    }
  };

  const getModuleToken = async () => {
    try {
      await contentqaMsalInstance.initialize();

      const redirectResponse =
        await contentqaMsalInstance.handleRedirectPromise();

      if (redirectResponse) {
        console.log("Redirect response received:", redirectResponse);
        contentqaMsalInstance.setActiveAccount(redirectResponse.account);
        setModuleAccessToken(redirectResponse.accessToken);
        return redirectResponse;
      }

      const accounts = contentqaMsalInstance.getAllAccounts();

      if (!accounts || accounts.length === 0) {
        console.log(
          "No accounts found - triggering redirect login for ContentQA",
        );

        await contentqaMsalInstance.loginRedirect({
          scopes: [import.meta.env.VITE_CONTENTQA_SCOPE],
        });

        return;
      }

      const tokenResponse = await contentqaMsalInstance.acquireTokenSilent({
        account: accounts[0],
        scopes: [import.meta.env.VITE_CONTENTQA_SCOPE],
        forceRefresh: false,
      });

      setModuleAccessToken(tokenResponse.accessToken);
      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log("Token expired - triggering redirect for ContentQA");
        await contentqaMsalInstance.acquireTokenRedirect({
          scopes: [import.meta.env.VITE_CONTENTQA_SCOPE],
        });
        return;
      } else {
        console.error("Error getting module token:", error);
        throw error;
      }
    }
  };

  const getPlatformToken = async () => {
    try {
      await msalInstance.initialize();

      const accounts = msalInstance.getAllAccounts();

      if (!accounts || accounts.length === 0) {
        console.log("No accounts found - triggering popup login for Platform");

        const loginResponse = await msalInstance.loginPopup({
          scopes: ["https://graph.windows.net/User.Read", "openid"],
        });

        msalInstance.setActiveAccount(loginResponse.account);

        if (loginResponse.accessToken) {
          await decodeAndSetUserDetails(loginResponse.accessToken);
        }

        return loginResponse;
      }

      const tokenResponse = await msalInstance.acquireTokenSilent({
        account: accounts[0],
        scopes: ["https://graph.windows.net/User.Read", "openid"],
        forceRefresh: false,
      });

      if (tokenResponse.accessToken) {
        console.log(
          "Decoding platform token to set user details",
          tokenResponse,
        );
        await decodeAndSetUserDetails(tokenResponse.accessToken);
      }

      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log("Token expired - triggering popup for Platform");
        const tokenResponse = await msalInstance.acquireTokenPopup({
          scopes: ["https://graph.windows.net/User.Read", "openid"],
        });

        if (tokenResponse.accessToken) {
          await decodeAndSetUserDetails(tokenResponse.accessToken);
        }

        return tokenResponse;
      } else {
        console.error("Error getting platform token:", error);
        throw error;
      }
    }
  };

  const decodeAndSetUserDetails = async (accessToken: string) => {
    if (accessToken?.split(".").length === 3) {
      try {
        const base64Url = accessToken.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(""),
        );

        const userDetails = JSON.parse(jsonPayload);

        // Ensure region is validated and set before API calls
        revalidateRegion();

        try {
          const authResponse = await apiService.post(
            "init",
            {},
            "platformUrl",
            accessToken,
          );

          setSessionDetails((prev) => {
            return {
              ...prev,
              work_session_id: WorkSessionID ?? "",
              work_group_id: WorkGroupID ?? "",
              user_id: authResponse?.data?.userid ?? "",
              client_id: authResponse?.data?.clientid ?? "",
              accessToken: accessToken,
              region: prev.region,
              language: prev.language,
              userDetails: {
                name: userDetails.name || "",
                email: userDetails.unique_name || "",
                givenName: userDetails.given_name || "",
              },
            };
          });

          // Init succeeded, confirm the region is ready
          setRegionStatus("ready");

          if (authResponse?.data?.userid) {
            try {
              const userInfo = await apiService.get(
                `user/${authResponse.data.userid}`,
                {},
                "platformUrl",
                accessToken,
              );

              if (userInfo.data?.rolename) {
                setRoleName(userInfo.data.rolename);
              }
              if (userInfo.data?.enterpriseid) {
                setSessionDetails((prev) => ({
                  ...prev,
                  enterpriseid: userInfo.data.enterpriseid,
                }));
              }
            } catch (userInfoError) {
              console.error("Failed to fetch user info:", userInfoError);
              setHasAccessError(true);
            }
          } else {
            console.log("User ID not available from init response");
            setHasAccessError(true);
          }
        } catch (initError) {
          console.error("Failed to initialize platform session:", initError);

          // Track the region that failed
          if (region) {
            setFailedRegion(region);
          }

          // Show region selection popup
          console.log("Init failed, showing region selection popup");
          setRegionStatus("missing");
          setSelectedRegion(null);
          setSessionDetails((prev) => ({
            ...prev,
            region: "",
          }));
        }
      } catch (e) {
        console.warn("Failed to decode access token:", e);
      }
    }
  };

  const logout = async () => {
    try {
      console.log("[SessionContext] 🚪 Logging out user...");

      setSessionDetails({
        work_session_id: "",
        work_group_id: "",
        project_id: "",
        client_id: "",
        user_id: "",
        accessToken: "",
        region: "",
        language: "",
        enterpriseid: "",
        userDetails: {
          name: "",
          email: "",
          givenName: "",
        },
      });

      setModuleAccessToken("");
      setRoleName("");
      setIsApproved(false);
      setSelectedRegion(null);
      setLanguage("");
      setRegionStatus("loading");
      setRegionError("");
      setFailedRegion(null);

      try {
        sessionStorage.clear();
      } catch (error) {
        console.error(
          "[SessionContext] Failed to clear sessionStorage:",
          error,
        );
      }

      try {
        await contentqaMsalInstance.initialize();
        const accounts = contentqaMsalInstance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          await contentqaMsalInstance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          });
        }
      } catch (error) {
        console.error(
          "[SessionContext] Error logging out from module MSAL:",
          error,
        );
      }

      try {
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          await msalInstance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          });
        }
      } catch (error) {
        console.error(
          "[SessionContext] Error logging out from platform MSAL:",
          error,
        );
      }
    } catch (error) {
      console.error("[SessionContext] Logout error:", error);
      window.location.href = window.location.origin;
    }
  };

  // Calculate available regions (exclude the failed region)
  const availableRegions = ALLOWED_REGIONS.filter(
    (r) => r !== failedRegion,
  );

  return (
    <SessionProviderContext.Provider
      value={{
        sessionDetails,
        setSessionDetails,
        getToken,
        getModuleToken,
        getPlatformToken,
        isJoinedRoom,
        setIsJoinedRoom,
        pageContent,
        setPageContent,
        roleName,
        setRoleName,
        isApproved,
        setIsApproved,
        hasAccessError,
        setHasAccessError,
        moduleAccessToken,
        setModuleAccessToken,
        logout,
        region,
        language,
        regionStatus,
        regionError,
        availableRegions,
        selectRegion,
        revalidateRegion,
      }}
    >
      {children}
    </SessionProviderContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionProviderContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
